/**
 * ticket-wallet.panel — the ONE image a wallet pass carries: the in-app card's
 * OWN hero region, re-rendered at the band's width. Apple bundles it as
 * `strip*.png` (375x98 pt for an event ticket, per PassKit's own image table).
 * It is not a band designed for a pass — it is the top of the card, so the
 * pass's first inch and the card's first inch are the same picture.
 *
 * THE CARD'S GEOMETRY IS REPRODUCED, THEN SCALED ONCE. The block is authored
 * at the card's own 384px design width — the card's 24px gutter, the card's
 * 24px top padding, the card's 336px content width, the card's own wordmark
 * tier (`stencilWordmarkTier`) — and the whole finished block is then scaled
 * by one factor, `bandWidth / 384`. Nothing is re-typeset for the band, so the
 * band's typographic scale IS the card's, off by exactly the ratio of the two
 * canvases (0.977 on Apple's strip).
 *
 * The band does not ask the fitted wordmark to fit a band. It asks it to fit
 * exactly the box it already fits on the card, and then scales that finished
 * block geometrically. The element's fit is a measured correction and it
 * converges at the card's proportions, where the card proves it on every
 * render; asked to fit a box three times wider and a third as tall it settles
 * on a scale that leaves the run overhanging its box, which on a signed pass is
 * a clipped wordmark nobody can fix after issuance.
 *
 * THE BAND'S GROUND IS THE CARD'S GROUND, NOT A PLATE. A contrasting plate
 * gives the band a hard edge and turns the pass's header into a section of its
 * own — a section the card does not have. `plate`/`mark` therefore arrive as
 * the card's own `{ background, ink }` pair (ticket-wallet.design.ts's
 * `walletPanelTreatment`), so the band composites seamlessly onto the pass
 * ground and reads as the top of one continuous card.
 *
 * APPLE'S ALLOTMENT IS SHORTER THAN THE CARD'S TALLEST TIER, and the ladder
 * absorbs it: 98 pt of strip is 100.4 px of card space, of which the card's own
 * top padding spends 24 — so a name short enough for the card's 104px tier
 * (whose line box is 109px) takes the tallest tier that actually fits instead.
 * That is a step on the card's own ladder, never a size invented for the band.
 *
 * THE UPLOADED EVENT MARK WINS WHERE THERE IS ONE, exactly as the card
 * prefers it: an event carrying its own designed wordmark renders that image
 * left-aligned and height-bounded, and only an event without one falls
 * through to the generated wordmark.
 *
 * NO INK BUT THE WORDMARK. The band is the card's hero region and the hero
 * region is the wordmark alone; every logistics fact below it is a field the
 * vendor draws in its own typography, and repeating one here would state it
 * twice on one pass.
 */
import { html, type TemplateResult } from "lit";
import {
	renderWordmarkFullWidth,
	STENCIL_CONTENT_WIDTH_PX,
	STENCIL_DESIGN_WIDTH_PX,
	STENCIL_GUTTER_PX,
	stencilWordmarkTier,
} from "./ticket-inapp.stencil-wordmark.ts";
import { ticketCardStyles } from "./ticket-inapp.styles.ts";

/**
 * Everything one panel render needs, as a JSON-safe shape: it crosses the
 * wire to the render service as a frame payload, so it carries resolved
 * strings rather than the card's own props object.
 */
export type WalletPanelPayload = {
	readonly eventName: string;
	/** The event's own uploaded wordmark, preferred over the generated one. */
	readonly logoUrl: string | null;
	/** The band's ground — the card's own. */
	readonly plate: string;
	/** The wordmark's own colour on that ground — the card's own ink. */
	readonly mark: string;
	readonly widthPx: number;
	readonly heightPx: number;
};

/** The card's own `.tkt-stencil-card` top padding, reproduced above the
 *  wordmark so the band's breathing room is the card's breathing room. */
const CARD_TOP_PADDING_PX = 24;

/** The card's own `.tkt-stencil-logo` height — the vertical budget an uploaded
 *  mark spends there, so an image and generated letterforms occupy the same
 *  band region they occupy on the card. */
const CARD_LOGO_HEIGHT_PX = 74;

export function renderWalletPanel(payload: WalletPanelPayload): TemplateResult {
	// One number maps the card's design width onto this band; every distance
	// inside the block below stays a card distance and rides that one scale.
	const bandScale = payload.widthPx / STENCIL_DESIGN_WIDTH_PX;
	const availableBoxPx = payload.heightPx / bandScale - CARD_TOP_PADDING_PX;
	const tier = stencilWordmarkTier(payload.eventName, availableBoxPx);
	const logoHeightPx = Math.min(CARD_LOGO_HEIGHT_PX, availableBoxPx);

	return html`
    <!-- The card's OWN stylesheet, mounted whole. The display wordmark is
         built from class-driven boxes — each character an inline-block so a
         transform applies to it at all, each word a nowrap box so the run
         breaks only at real spaces — and without those rules the spans
         compute as plain inline text that wraps and takes no transform. A
         panel that re-declared the display face instead of mounting this
         drifts from the card the moment the card's face or tracking moves. -->
    ${ticketCardStyles}
    <div
      style="width:${payload.widthPx}px;height:${payload.heightPx}px;background-color:${payload.plate};color:${payload.mark};overflow:hidden;box-sizing:border-box;"
    >
      <div
        style="width:${STENCIL_DESIGN_WIDTH_PX}px;padding:${CARD_TOP_PADDING_PX}px ${STENCIL_GUTTER_PX}px 0;box-sizing:border-box;transform:scale(${bandScale});transform-origin:left top;"
      >
        ${
					payload.logoUrl
						? html`
            <!-- object-contain and a bounded height, never object-cover: an
                 event's own mark is a designed asset and cropping it is a
                 different mark. -->
            <img
              src=${payload.logoUrl}
              alt=${payload.eventName}
              style="height:${logoHeightPx}px;width:auto;max-width:${STENCIL_CONTENT_WIDTH_PX}px;object-fit:contain;object-position:left center;display:block;"
            />
          `
						: html`
            <!-- No white-space override here. The fitted wordmark measures
                 its own content and applies one scale correction against that
                 measurement; forcing nowrap from the parent changes the
                 content's natural width out from under that measurement, and
                 the stale scale then leaves the run wider than its box —
                 which on a band means a CLIPPED wordmark. -->
            <div class="tkt-card-display" style="width:${STENCIL_CONTENT_WIDTH_PX}px;">
              ${renderWordmarkFullWidth(payload.eventName, {
								widthPx: STENCIL_CONTENT_WIDTH_PX,
								heightPx: tier.boxPx,
								fontSizePx: tier.fontSizePx,
							})}
            </div>
          `
				}
      </div>
    </div>
  `;
}
